/* global PRIVATE_MEDIA_HOST, API_URL */
/**
 * SPEC-010 Phase 2: CloudFront signed-cookie readiness.
 *
 * Segment/manifest requests to the PRIVATE video distribution must carry the
 * CloudFront-* cookies the playlist endpoint sets, which requires
 * credentialed XHR (withCredentials). Public hosts must NOT be credentialed —
 * their CORS config doesn't allow credentials and the requests would fail.
 */

// PRIVATE_MEDIA_HOST is env-driven (mirrors the backend's PRIVATE_MEDIA_URL:
// videos-dev.stanwinstonschool.com on dev, videos.stanwinstonschool.com in
// production). The pattern fallback keeps the cutover safe if the env var is
// missing from a build — a silently absent flag here would 403 every segment
// once manifests go plain-URL (see the GA4 measurement-id incident).
const FALLBACK_HOST_PATTERN = /^videos(-[\w-]+)?\.stanwinstonschool\.com$/

// Resolve a URL's host, tolerating protocol-relative ("//host/…") and
// relative forms by supplying a base. Production API_URL is protocol-relative
// (//api.stanwinstonschool.com); `new URL()` without a base THROWS on that,
// which previously made every host predicate return false → withCredentials
// was never set → the CloudFront Set-Cookie was dropped and segments 403'd.
const BASE_ORIGIN =
  (typeof window !== 'undefined' &&
    window.location &&
    window.location.origin) ||
  'https://www.stanwinstonschool.com'
const hostOf = (url) => {
  if (!url) return null
  try {
    return new URL(url, BASE_ORIGIN).host
  } catch (_) {
    return null
  }
}

export const isPrivateVideoUrl = (url) => {
  const host = hostOf(url)
  if (!host) return false
  if (typeof PRIVATE_MEDIA_HOST !== 'undefined' && PRIVATE_MEDIA_HOST)
    return host === PRIVATE_MEDIA_HOST
  return FALLBACK_HOST_PATTERN.test(host)
}

const apiHost = () =>
  typeof API_URL !== 'undefined' && API_URL ? hostOf(API_URL) : null

// The playlist MANIFESTS are served by the API host (which also refreshes
// the signed cookies on every manifest response). A cross-origin XHR only
// stores/sends cookies when credentialed, so JW Player's manifest requests
// to the API must be credentialed too — otherwise the Set-Cookie refresh is
// silently discarded and later segment requests go out cookieless.
export const isApiVideoUrl = (url) => {
  const host = hostOf(url)
  const api = apiHost()
  return Boolean(host && api && host === api)
}

export const isCredentialedVideoUrl = (url) =>
  isPrivateVideoUrl(url) || isApiVideoUrl(url)

// True when any source targets a credentialed host. The player sets a
// setup-level withCredentials from this, so credentialing does NOT rely on JW
// propagating the per-source flag to hls.js's manifest/fragment XHRs (which we
// have not confirmed it does). Public-only playlists (some special videos)
// stay uncredentialed so their CORS is not broken.
export const playlistRequiresCredentials = (playlist = []) =>
  playlist.some((item) =>
    (item.sources || []).some((source) => isCredentialedVideoUrl(source.file))
  )

// SPEC-010 FR-7: the signed-URL manifest variant is selected per request by
// `signed=1` on the playlist URL itself. The API's master manifest propagates
// it to every variant playlist, whose segment URLs then carry CloudFront
// query-string signatures instead of relying on cookies — which is what a
// cast receiver needs. The /user/ and /special-videos/ endpoints ignore the
// flag, so the player rewrites its own source URLs. Idempotent.
export const signedVariantUrl = (url) => {
  try {
    const parsed = new URL(url, BASE_ORIGIN)
    parsed.searchParams.set('signed', '1')
    return parsed.href
  } catch (_) {
    return url
  }
}

export const withSignedVariant = (playlist = []) =>
  playlist.map((item) => ({
    ...item,
    sources: (item.sources || []).map((source) =>
      isApiVideoUrl(source.file)
        ? { ...source, file: signedVariantUrl(source.file) }
        : source
    )
  }))

// Mark private-host and API-host sources so JW Player performs credentialed
// requests for their manifests/segments (and flags the media element for
// Safari's native HLS path). Other hosts pass through untouched.
export const withPrivateVideoCredentials = (playlist = []) =>
  playlist.map((item) => {
    const sources = (item.sources || []).map((source) =>
      isCredentialedVideoUrl(source.file)
        ? { ...source, withCredentials: true }
        : source
    )
    return { ...item, sources }
  })
